import React, { useState } from "react";
import { Mail, MapPin, MessageCircle, Phone, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { COMPANY } from "../lib/constants";
import { submitContact } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Name, phone and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Message received. We'll respond shortly.");
      setForm({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="bg-[var(--m-bg-2)] section-pad pt-32 md:pt-44">
        <div className="container-x">
          <span className="eyebrow">Contact</span>
          <div className="gold-divider mt-4" />
          <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[1] tracking-tight max-w-4xl">
            Let's <em className="italic text-[var(--m-teal)]">talk</em> about your build.
          </h1>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x grid lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-5">
            <h2 className="font-display text-3xl">Reach the engineer directly.</h2>
            <div className="gold-divider mt-4" />

            <div className="mt-10 space-y-8">
              <ContactRow icon={Phone} label="Call" value={COMPANY.phone} href={`tel:${COMPANY.phoneRaw}`} testId="contact-phone" />
              <ContactRow
                icon={MessageCircle}
                label="WhatsApp"
                value={COMPANY.phone}
                href={`https://wa.me/${COMPANY.whatsapp}`}
                testId="contact-whatsapp"
              />
              <ContactRow icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} testId="contact-email" />
              <ContactRow
                icon={MapPin}
                label="Office"
                value={COMPANY.address}
                href={`https://www.google.com/maps?q=${encodeURIComponent(COMPANY.mapsQuery)}`}
                testId="contact-address"
              />
            </div>

            <div className="mt-12 aspect-[4/3] border border-[var(--m-border)] overflow-hidden" data-testid="contact-map">
              <iframe
                title="Agartha Construction Office Location"
                src={`https://www.google.com/maps?q=${encodeURIComponent(COMPANY.mapsQuery)}&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-7">
            <div className="border border-[var(--m-border)] bg-[var(--m-bg)] p-10">
              <h2 className="font-display text-3xl">Send a message.</h2>
              <p className="mt-3 text-sm text-[var(--m-ink-2)]">
                For quick responses, WhatsApp is fastest. For detailed inquiries, fill the form below.
              </p>

              <form onSubmit={onSubmit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="contact-form">
                <div>
                  <label className="label-tiny">Full Name *</label>
                  <input className="input-line" required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name" />
                </div>
                <div>
                  <label className="label-tiny">Phone *</label>
                  <input className="input-line" required value={form.phone} onChange={set("phone")} placeholder="+91 9XXXX XXXXX" data-testid="contact-phone-input" />
                </div>
                <div>
                  <label className="label-tiny">Email</label>
                  <input className="input-line" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="label-tiny">Subject</label>
                  <input className="input-line" value={form.subject} onChange={set("subject")} placeholder="What's this about?" data-testid="contact-subject" />
                </div>
                <div className="md:col-span-2">
                  <label className="label-tiny">Message *</label>
                  <textarea
                    className="input-line resize-none"
                    rows={4}
                    required
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell us about your project..."
                    data-testid="contact-message"
                  />
                </div>
                <div className="md:col-span-2 pt-2">
                  <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50" data-testid="contact-submit">
                    {submitting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                    {submitting ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

const ContactRow = ({ icon: Icon, label, value, href, testId }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="group flex gap-5 items-start" data-testid={testId}>
    <div className="w-12 h-12 flex items-center justify-center border border-[var(--m-border)] group-hover:border-[var(--m-teal)] group-hover:bg-[var(--m-teal)] transition-all flex-shrink-0">
      <Icon size={18} className="text-[var(--m-teal)] group-hover:text-white transition-colors" strokeWidth={1.5} />
    </div>
    <div>
      <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--m-ink-3)] font-medium">{label}</div>
      <div className="text-base text-[var(--m-ink)] mt-1 group-hover:text-[var(--m-teal)] transition-colors leading-relaxed">{value}</div>
    </div>
  </a>
);
